import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { MouseEvent } from "react";
import { SIGNED_URL_BATCH_MAX, type SignedUrl } from "@buildpy/api/client";

import { api } from "@/api/client";
import { queryClient, SIGNED_URL_KEY } from "@/lib/query";

// 서명할 수 있는 화면 주소(/app/…) — api 의 SIGNABLE_PATHS 와 같은 여섯 갈래.
const SIGNABLE = [
  /^\/(?:app|api)\/projects\/[^/?#]+\/photos\/[^/?#]+$/,
  /^\/(?:app|api)\/projects\/[^/?#]+\/messages\/[^/?#]+\/photo$/,
  /^\/(?:app|api)\/projects\/[^/?#]+\/change-orders\/[^/?#]+\/signature$/,
  /^\/(?:app|api)\/projects\/[^/?#]+\/as\/[^/?#]+\/photos\/[^/?#]+$/,
  /^\/(?:app|api)\/portfolio\/[^/?#]+\/export$/,
  /^\/(?:app|api)\/settings\/export$/,
];

// 만료 2분 전에 새로 받는다 — 받은 주소로 막 그린 img 가 도중에 만료되지 않게.
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

// /app/… 화면 주소를 서명할 /api/… 주소로 — 서명 대상이 아니면 null.
export const signableApiPath = (path: string): string | null =>
  SIGNABLE.some((pattern) => pattern.test(path)) ? path.replace(/^\/app\//, "/api/") : null;

const signAll = async (apiPaths: readonly string[]): Promise<Map<string, SignedUrl>> => {
  const out = new Map<string, SignedUrl>();
  for (let i = 0; i < apiPaths.length; i += SIGNED_URL_BATCH_MAX) {
    const chunk = apiPaths.slice(i, i + SIGNED_URL_BATCH_MAX);
    const signed = await api.signUrls(chunk);
    chunk.forEach((path, index) => out.set(path, signed[index]));
  }
  return out;
};

const refreshInOf = (signed: Map<string, SignedUrl> | undefined): number => {
  if (!signed || signed.size === 0) return 0;
  const soonest = Math.min(...[...signed.values()].map((s) => Date.parse(s.expires_at)));
  return Math.max(soonest - Date.now() - REFRESH_MARGIN_MS, 10_000);
};

// 주소 여럿을 한 요청으로 서명받는다(발급도 분당 한도를 같이 쓴다) — 돌려준 함수가 화면 주소 → 서명 주소, 받기 전엔 undefined.
export const useSignedUrls = (paths: readonly string[]): ((path: string) => string | undefined) => {
  const apiPaths = [...new Set(paths.map(signableApiPath).filter((p): p is string => p !== null))].sort();
  const { data } = useQuery(
    {
      queryKey: [SIGNED_URL_KEY, ...apiPaths],
      queryFn: () => signAll(apiPaths),
      enabled: apiPaths.length > 0,
      placeholderData: keepPreviousData,
      staleTime: (query) => refreshInOf(query.state.data),
      refetchInterval: (query) => (query.state.data?.size ? refreshInOf(query.state.data) : false),
    },
    queryClient
  );
  return (path) => {
    const apiPath = signableApiPath(path);
    if (apiPath === null) return path;
    return data?.get(apiPath)?.url;
  };
};

const downloadSigned = async (apiPath: string, download: string): Promise<void> => {
  let url = apiPath;
  try {
    url = (await api.signUrls([apiPath]))[0].url;
  } catch (error) {
    console.error("[signed-url]", error);
  }
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = download;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
};

// 내려받기 링크 onClick — 누를 때 서명받아 그 주소로 내려받는다(서명이 안 되면 서명 없는 주소로 — 401 은 api 가 알린다).
export const signedDownload = (path: string) => (event: MouseEvent<HTMLAnchorElement>): void => {
  const apiPath = signableApiPath(path);
  if (apiPath === null || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  void downloadSigned(apiPath, event.currentTarget.getAttribute("download") ?? "");
};
